import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useMembership } from "@/context/MembershipContext";
import { http, apiError } from "@/lib/api";
import { Barbell, ChartLine, ChatCircle, Check, SignOut, Lock, ArrowRight, Star } from "@phosphor-icons/react";

/**
 * SubscribePage — the paywall for the Lazy Lean Method 12-Week Coaching Program.
 * Presents two purchase options: Pay in Full (best value, one-time $997) or
 * Flexible Payment Plan (12 × $89/wk). Clicking either kicks off a Stripe
 * Checkout Session with the appropriate mode. FOUNDER-style promo codes work
 * on both because allow_promotion_codes is enabled server-side.
 */
export default function SubscribePage() {
  const { user, logout } = useAuth();
  const { membership, refresh } = useMembership();  // eslint-disable-line no-unused-vars
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");
  const [err, setErr] = useState("");

  const active = membership?.active;
  const days = membership?.days_remaining ?? 0;
  const programEndsAt = membership?.program_ends_at ? new Date(membership.program_ends_at) : null;
  const plans = membership?.plans || {};
  const full = plans.full;
  const plan = plans.plan;

  const checkout = async (which) => {
    setErr("");
    setLoading(which);
    try {
      const { data } = await http.post("/payments/checkout", {
        origin_url: window.location.origin,
        plan: which,
      });
      if (data?.url) { window.location.href = data.url; return; }
      setErr("Could not start checkout. Try again.");
    } catch (e) { setErr(apiError(e, "Could not start checkout.")); }
    finally { setLoading(""); }
  };

  const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#262626]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF3333] flex items-center justify-center">
              <Barbell weight="bold" size={18} color="#fff" />
            </div>
            <span className="font-oswald text-xl font-bold uppercase tracking-widest">
              Lazy Lean Method<sup className="font-manrope text-[11px] font-semibold tracking-normal ml-0.5 -top-1.5 relative">™</sup>
            </span>
          </div>
          <button onClick={handleLogout} data-testid="subscribe-logout" className="id-btn-ghost text-xs">
            <SignOut size={14} weight="bold" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 md:py-16">
        <div className="id-label mb-3">— The Lazy Lean Method™ —</div>
        <h1 className="id-h1 mb-3">
          {active
            ? "You&apos;re in."
            : <>12-Week<br/><span className="text-[#FF3333]">Coaching Program</span></>}
        </h1>
        <p className="text-zinc-400 font-manrope text-lg max-w-2xl">
          {active
            ? `${days} day${days === 1 ? "" : "s"} remaining${programEndsAt ? ` · program ends ${programEndsAt.toLocaleDateString()}` : ""}.`
            : `Hey ${user?.name?.split(" ")[0] || "mate"} — pick your payment option and you're straight in. Same program either way.`}
        </p>

        {!active && (
          <div className="mt-8 border border-[#FF3333]/40 bg-[#FF3333]/5 px-5 py-4 flex items-start gap-3" data-testid="paywall-banner">
            <Lock size={18} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
            <div className="text-sm font-manrope text-zinc-200">
              Complete payment below to unlock your dashboard, custom plan, 7 Lazy Lean Habits™, and direct coach chat.
            </div>
          </div>
        )}

        {err && <div className="mt-6 border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333]" data-testid="subscribe-error">{err}</div>}

        {!active && full && plan && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="subscribe-plans">
            {/* ==================== Pay in Full ==================== */}
            <div
              className="relative id-card border-[#FF3333] shadow-[inset_0_0_60px_rgba(255,51,51,0.08)]"
              data-testid="plan-full-card"
            >
              <div className="absolute -top-3 left-6 bg-[#FF3333] text-white px-3 py-1 font-oswald text-[10px] uppercase tracking-[0.2em] font-bold flex items-center gap-1.5">
                <Star size={11} weight="fill" /> Best Value — Pay in Full
              </div>
              <div className="id-label mb-2 mt-2">One-Time Payment</div>
              <div className="flex items-baseline gap-2">
                <span className="font-oswald text-6xl font-bold text-white" data-testid="plan-full-amount">${Number(full.amount).toFixed(0)}</span>
                <span className="text-zinc-400 font-manrope text-lg">AUD</span>
              </div>
              <div className="text-xs text-zinc-500 font-manrope mt-1">
                {full.billed_as}
              </div>

              <ul className="mt-6 space-y-3">
                {full.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 font-manrope">
                    <Check size={18} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => checkout("full")}
                disabled={!!loading}
                data-testid="checkout-full-button"
                className="mt-6 id-btn-primary w-full text-base"
              >
                {loading === "full" ? "Redirecting..." : `Pay in Full — $${Number(full.amount).toFixed(0)}`}
              </button>
            </div>

            {/* ==================== Payment Plan ==================== */}
            <div className="id-card" data-testid="plan-plan-card">
              <div className="id-label mb-2 mt-2">Flexible Payment Plan</div>
              <div className="flex items-baseline gap-2">
                <span className="font-oswald text-6xl font-bold text-white" data-testid="plan-plan-amount">${Number(plan.amount).toFixed(0)}</span>
                <span className="text-zinc-400 font-manrope text-lg">/ week</span>
              </div>
              <div className="text-xs text-zinc-500 font-manrope mt-1">
                {plan.billed_as} · Total investment: ${Number(plan.total).toFixed(0)} AUD
              </div>

              <ul className="mt-6 space-y-3">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-zinc-200 font-manrope">
                    <Check size={18} weight="bold" className="text-[#FF3333] flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => checkout("plan")}
                disabled={!!loading}
                data-testid="checkout-plan-button"
                className="mt-6 id-btn-secondary w-full text-base"
              >
                {loading === "plan" ? "Redirecting..." : `Start — $${Number(plan.amount).toFixed(0)} / wk × ${plan.weeks}`}
              </button>
            </div>
          </div>
        )}

        {/* WEEK 13+ Continued Coaching banner — visible to unpaid clients so they
            know what happens after the 12-week program ends. */}
        {!active && plans.continued && (
          <div
            className="mt-6 border border-[#262626] bg-[#0F0F0F] px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4"
            data-testid="continued-coaching-banner"
          >
            <div className="flex-1 min-w-0">
              <div className="id-label text-[#FF3333] mb-1.5">— Week 13+ · Continued Coaching —</div>
              <div className="font-oswald text-2xl uppercase tracking-wide text-white leading-tight">
                Keep your momentum for <span className="text-[#FF3333]">${Number(plans.continued.amount).toFixed(0)}/wk</span>
              </div>
              <p className="text-zinc-400 font-manrope text-sm mt-1.5 leading-relaxed">
                After you finish the 12-week program you&apos;ll be fitter, sharper and know the system inside out. Keep the coach, chat, habit tracker and progress dashboard for a reduced ${Number(plans.continued.amount).toFixed(0)}/week — cancel anytime.
              </p>
              <p className="text-xs text-zinc-500 font-manrope mt-2 leading-relaxed">
                Payment Plan clients auto-transition at week 13. Pay in Full clients get an opt-in prompt near the end of week 12.
              </p>
            </div>
          </div>
        )}

        {/* Active pay-in-full members nearing the end of their program get an opt-in
            prompt to continue at $69/wk. Payment-plan clients auto-roll so they don't
            see this. */}
        {active && membership?.program_plan === "full" && days <= 21 && plans.continued && (
          <div className="mt-8 border-2 border-[#FF3333] bg-[#FF3333]/5 px-6 py-6" data-testid="continue-coaching-cta">
            <div className="id-label text-[#FF3333] mb-2">— Your program ends in {days} day{days === 1 ? "" : "s"} —</div>
            <div className="font-oswald text-2xl md:text-3xl uppercase tracking-wide text-white leading-tight mb-2">
              Ready to lock in Continued Coaching?
            </div>
            <p className="text-zinc-300 font-manrope text-sm mb-4 max-w-2xl">
              You&apos;ve done the work. Keep your coach, chat, habit tracker and dashboard active for <strong className="text-white">${Number(plans.continued.amount).toFixed(0)}/week</strong> ongoing — cancel anytime. New billing starts on your program end date.
            </p>
            <button
              onClick={() => checkout("continued")}
              disabled={!!loading}
              data-testid="checkout-continued-button"
              className="id-btn-primary"
            >
              {loading === "continued" ? "Redirecting..." : `Continue Coaching — $${Number(plans.continued.amount).toFixed(0)}/wk`}
            </button>
          </div>
        )}

        {/* Active member — link back to dashboard */}
        {active && (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="id-card lg:col-span-2">
              <div className="id-label mb-2">Program status</div>
              <div className="font-oswald text-2xl uppercase tracking-wide text-white">
                {membership?.program_plan === "full" ? "Paid in Full · $997" : `Payment Plan · $${Number(plan?.amount ?? 89).toFixed(0)}/wk × ${plan?.weeks ?? 12}`}
              </div>
              <p className="text-zinc-400 font-manrope text-sm mt-2">
                {days} day{days === 1 ? "" : "s"} of coaching remaining{programEndsAt ? `. Program ends ${programEndsAt.toLocaleDateString()}.` : "."}
              </p>
              <Link to="/app" data-testid="continue-to-app" className="mt-6 id-btn-primary inline-flex">
                Open your dashboard <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="id-card">
              <div className="id-label mb-3">What&apos;s in the portal</div>
              <ul className="space-y-3 text-sm text-zinc-300 font-manrope">
                <li className="flex items-center gap-3"><Barbell size={16} weight="bold" className="text-[#FF3333]" /> Your custom plan</li>
                <li className="flex items-center gap-3"><ChartLine size={16} weight="bold" className="text-[#FF3333]" /> Progress charts</li>
                <li className="flex items-center gap-3"><ChatCircle size={16} weight="bold" className="text-[#FF3333]" /> 1:1 coach chat</li>
              </ul>
            </div>
          </div>
        )}

        {!active && (
          <div className="mt-8 text-xs text-zinc-500 font-manrope leading-relaxed max-w-3xl">
            Payments processed securely by Stripe. Got a promo code? You&apos;ll be able to enter it on the checkout screen. The 12 weeks of coaching start immediately after payment — you&apos;ll get access to your dashboard, coach chat, and habit tracker straight away.
          </div>
        )}
      </main>
    </div>
  );
}
